import React from "react";
import { createRoot, Root } from "react-dom/client";

// Page component imports
import { AdminDashboardPage } from "@/components/pages/admin-dashboard";
import { CheckoutSuccessPage } from "@/components/pages/checkout-success";
import { CheckoutPage } from "@/components/pages/checkout";
import { CohortDetailPage } from "@/components/pages/cohort-detail";
import { CohortsPage } from "@/components/pages/cohorts";
import { DashboardPage } from "@/components/pages/dashboard";
import { HomePage } from "@/components/pages/home";
import { LandingPage } from "@/components/pages/landing";
import { LoginPage } from "@/components/pages/login";
import { ManageCohortsPage } from "@/components/pages/manage-cohorts";
import { ManageModulesPage } from "@/components/pages/manage-modules";
import { ModuleEditorPage } from "@/components/pages/module-editor";
import { ModulesPage } from "@/components/pages/modules";
import { MyStartupPage } from "@/components/pages/my-startup";
import { ProfileSetupPage } from "@/components/pages/profile-setup";
import { QuizEditorPage } from "@/components/pages/quiz-editor";
import { QuizLibraryPage } from "@/components/pages/quiz-library";
import { SignupPage } from "@/components/pages/signup";
import { ActivityLearningPage } from "@/components/pages/activity-learning";
import { VideoLearningPage } from "@/components/pages/video-learning";
import { VideosLibraryPage } from "@/components/pages/videos-library";
import { TipTapEditor } from "@/components/academician/tiptap-editor";
import { ValidationScoreboard } from "@/components/academician/validation-scoreboard";

// Component registry - maps data-react-component names to React components
const COMPONENTS: Record<string, React.ComponentType<any>> = {
  ActivityLearningPage,
  AdminDashboardPage,
  CheckoutSuccessPage,
  CheckoutPage,
  CohortDetailPage,
  CohortsPage,
  DashboardPage,
  HomePage,
  LandingPage,
  LoginPage,
  ManageCohortsPage,
  ManageModulesPage,
  ModuleEditorPage,
  ModulesPage,
  MyStartupPage,
  ProfileSetupPage,
  QuizEditorPage,
  QuizLibraryPage,
  SignupPage,
  VideoLearningPage,
  VideosLibraryPage,
  TipTapEditor,
  ValidationScoreboard,
};

// Track mounted roots to prevent double-mounting and enable cleanup
const mountedRoots = new Map<Element, Root>();

function mountReactComponents() {
  const containers = document.querySelectorAll<HTMLElement>(
    "[data-react-component]"
  );

  containers.forEach((container) => {
    // Skip if already mounted
    if (mountedRoots.has(container)) return;

    const componentName = container.dataset.reactComponent;
    if (!componentName) return;

    const Component = COMPONENTS[componentName];
    if (!Component) {
      console.warn(
        `React component "${componentName}" not found in registry. Available: ${Object.keys(COMPONENTS).join(", ")}`
      );
      return;
    }

    // Parse props from data-react-props JSON attribute
    let props = {};
    const propsJson = container.dataset.reactProps;
    if (propsJson) {
      try {
        props = JSON.parse(propsJson);
      } catch (e) {
        console.error(
          `Failed to parse props for "${componentName}":`,
          e
        );
      }
    }

    const root = createRoot(container);
    root.render(React.createElement(Component, props));
    mountedRoots.set(container, root);
  });
}

function unmountReactComponents() {
  mountedRoots.forEach((root, container) => {
    root.unmount();
  });
  mountedRoots.clear();
}

// Mount on initial page load
document.addEventListener("DOMContentLoaded", mountReactComponents);

// Mount on Turbo navigation (new page loaded)
document.addEventListener("turbo:load", mountReactComponents);

// Unmount before Turbo caches the page (prevents stale React trees)
document.addEventListener("turbo:before-cache", unmountReactComponents);
